import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  GridItem,
  Select,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Text,
  Heading,
  VStack,
  Button,
  HStack,
} from "@chakra-ui/react";
// import { Function } from "@chakra-ui/icons";
import { MaterialSymbol } from "react-material-symbols";
import KDEPlot from "./KDEPlot";
import BoxPlot from "./BoxPlot";
import { kernels } from "./kernels";
import { summaryStats } from "./utils";
import { ENDPOINT } from "../api";


// import ScatterPlot from "./ScatterPlot";

const sortBy = (list, key = null) => {
  if (key === null || key === "Index") {
    return list.sort((a, b) => (a?.index ?? 0) - (b?.index ?? 0));
  }
  return list.sort((a, b) => (a?.stats?.[key] ?? 0) - (b?.stats?.[key] ?? 0));
};

function formatNumber(
  n,
  { sig = 3, style = "text", expThresholdLow = -2, expThresholdHigh = 3 } = {}
) {
  if (!Number.isFinite(n)) return String(n);
  if (n === 0) return "0";

  const abs = Math.abs(n);
  const e = Math.floor(Math.log10(abs)); // base-10 exponent

  // Use scientific notation only for very small/large numbers
  if (e < expThresholdLow || e >= expThresholdHigh) {
    let [m, exp] = n.toExponential(sig - 1).split("e"); // "1.23e-5"
    exp = String(parseInt(exp, 10));

    if (style === "html") return `${m} × 10<sup>${exp}</sup>`;
    if (style === "latex") return `${m}\\times 10^{${exp}}`;
    if (style === "unicode") {
      const sup = {
        "-": "⁻",
        0: "⁰",
        1: "¹",
        2: "²",
        3: "³",
        4: "⁴",
        5: "⁵",
        6: "⁶",
        7: "⁷",
        8: "⁸",
        9: "⁹",
      };
      const esup = [...exp].map((ch) => sup[ch] ?? ch).join("");
      return `${m} × 10${esup}`;
    }
    return `${m} × 10^${exp}`; // plain text
  }

  // Otherwise: round to sig significant digits and print as a normal decimal
  // scale = 10^(e - sig + 1) so rounding keeps sig significant digits
  const scale = Math.pow(10, e - sig + 1);
  let rounded = Math.round(n / scale) * scale;

  // Avoid "-0"
  if (Object.is(rounded, -0)) rounded = 0;

  // Number of fractional digits needed to *show* sig significant digits
  const frac = Math.max(0, sig - 1 - e);
  return rounded.toLocaleString("en-US", {
    useGrouping: false,
    minimumFractionDigits: frac,
    maximumFractionDigits: frac,
  });
}

// Examples
// console.log(formatSci(0.000012));                // "1.2 × 10^-5"
// console.log(formatSci(0.000012, 3));             // "1.20 × 10^-5"
// console.log(formatSci(0.000012, 2, 'html'));     // "1.2 × 10<sup>-5</sup>"
// console.log(formatSci(0.000012, 2, 'latex'));    // "1.2\\times 10^{-5}"
// console.log(formatSci(0.000012, 2, 'unicode'));  // "1.2 × 10⁻⁵"

const GroupSummary = ({ data }) => {
  // console.log("X", data);
  const gstats = "data" in data ? summaryStats(data.data) : {};

  return (
    <Box>
      <HStack>
        <Box
          w="16px"
          h="16px"
          bg={data.color}
          opacity={0.7}
          mr={0}
          borderRadius="50%"
          flexShrink={0}
        ></Box>
        <Text>{data.label}</Text>
      </HStack>
      <Text>mean: {formatNumber(gstats?.mean ?? 0)}</Text>
      <Text>median: {formatNumber(gstats?.med ?? 0)}</Text>
    </Box>
  );
};

const BoxPlots = () => {
  const [winWidth, setWinWidth] = useState(window.innerWidth);
  // const [dailyEntriesPP, setDailyEntriesPP] = useState([]);
  // const [daysAvgK, setDaysAvgK] = useState(3);
  // const [daysMax, setDaysMax] = useState(150);
  const [featureGroups, setFeatureGroups] = useState([
    "MFCC",
    "Spectral",
    "Glottal",
    "Period",
    "Jitter",
    "Shimmer",
    "Amplitude",
    "Formant",
    "Forced align",
    "VAD",
  ]);
  const [funcIds, setFuncIds] = useState([
    { key: "x_mean", text: "Mean (μ)" },
    { key: "x_median", text: "Median" },
    { key: "x_std", text: "Standard deviation (σ)" },
    { key: "x_cov", text: "Coefficient of variation (CV)" },
    { key: "dt_x_mean", text: "Mean of delta (μΔ)" },
    { key: "dt_x_std", text: "Std.dev. of delta (σΔ)" },
  ]);
  const [featureNames, setFeatureNames] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState("OQ1_mean");
  // const [patientIds, setPatientIds] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("Spectral");
  const [selectedFunc, setSelectedFunc] = useState({
    key: "x_mean",
    text: "Mean",
  });
  const [selectedKernel, setSelectedKernel] = useState(
    Object.keys(kernels)[0] ?? ""
  );

  const [sortOptions, setSortOption] = useState([
    { key: "Index", text: "Index" },
    { key: "p_mwu", text: "p-value (Mann-Whitney U)" },
  ]);
  const [selectedSortBy, setSelectedSortBy] = useState({
    key: "Index",
    text: "Index",
  });

  const [featureInfo, setFeatureInfo] = useState(null);
  // const [totalPatients, setTotalPatients] = useState(0);

  // const [currentPage, setCurrentPage] = useState(1);
  // const patientsPerPage = 15;

  // const ENDPOINT = "http://127.0.0.1:8000";

  const [featureValues, setFeatureValues] = useState([
    { key: "test", data: [{ data: [], color: "steelblue", label: "A" }] },
    { key: "test2", data: [{ data: [], color: "steelblue", label: "A" }] },
  ]);

  const fetchFeatureValues = async () => {
    try {
      const response = await fetch(
        `${ENDPOINT}/feature-values?group=${selectedGroup}&func=${selectedFunc.key}`
      );
      if (!response.ok) {
        throw new Error(`Error fetching feature info: ${response.statusText}`);
      }
      const jsonData = await response.json();
      setFeatureValues(jsonData.data);
      console.log(jsonData);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      await fetchFeatureValues();
    };
    fetchInitialData();
  }, []);

  const handleGroupChange = async (e) => {
    const selected = e.target.value;
    setSelectedGroup(selected);
  };

  const handleFuncChange = async (e) => {
    const value = e.target.value;
    const opt = funcIds.find((o) => String(o.text) === value);
    setSelectedFunc(opt);
  };

  const handleSortChange = async (e) => {
    const value = e.target.value;
    const opt = sortOptions.find((o) => String(o.text) === value);
    console.log("OpT", opt);
    setSelectedSortBy(opt);
  };

  const handleKernelChange = async (e) => {
    const value = e.target.value;
    setSelectedKernel(value);
  };

  useEffect(() => {
    const fetchDataForPatientAndFeature = async () => {
      fetchFeatureValues();
    };

    fetchDataForPatientAndFeature();
    console.log("sort", sortBy(featureValues, "p_mwu"));
    // fetchFeatureInfo(selectedFeature);
  }, [selectedGroup, selectedFunc]);


  return (
    <Grid
      templateColumns={{ base: "1fr", md: "3fr 1fr" }}
      gap={3}
      p={4}
      mt={20}
    >
      {/* Controls (Total Patients, Patient ID, Features, Legend, and Pagination) */}
      <GridItem order={{ base: 1, md: 2 }}>
        <Box mb={8}>
          <HStack spacing="1" align="center" mb={6}>
            <Heading as="h5" size="m" mb={0}>
              Select feature group
            </Heading>
            <MaterialSymbol
              // as={MaterialSymbol}
              icon="list_alt"
              size={22}
              // fontSize="1.25em" // scales with text size
              verticalAlign="text-bottom"
              fill={0}
              grade={0}
              weight={300}
              aria-hidden // decorative
            />
          </HStack>

          <Select value={selectedGroup} onChange={handleGroupChange}>
            {featureGroups.map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </Select>
        </Box>

        {/* Feature selection, Mean Days, Max Days Sliders */}
        <Box p={4} boxShadow="md" borderRadius="md" mb={8}>
          <VStack align="stretch" spacing={4}>
            <Box>
              <HStack spacing="1" align="center" mb={2}>
                <Text m={0}>Functional</Text>
                <MaterialSymbol
                  // as={MaterialSymbol}
                  icon="function"
                  size={22}
                  // fontSize="1.25em" // scales with text size
                  verticalAlign="text-bottom"
                  fill={1}
                  grade={0}
                  weight={300}
                  aria-hidden // decorative
                />
              </HStack>
              <Select value={selectedFunc.text} onChange={handleFuncChange}>
                {funcIds.map((d) => (
                  <option key={d.key} value={d.text}>
                    {d.text}
                  </option>
                ))}
              </Select>
            </Box>

            <Box>
              <HStack spacing="1" align="center" mb={2}>
                <Text m={0}>Sort by</Text>
                <MaterialSymbol
                  // as={MaterialSymbol}
                  icon="sort"
                  size={22}
                  // fontSize="1.25em" // scales with text size
                  verticalAlign="text-bottom"
                  fill={1}
                  grade={0}
                  weight={300}
                  aria-hidden // decorative
                />
              </HStack>
              <Select value={selectedSortBy.text} onChange={handleSortChange}>
                {sortOptions.map((d) => (
                  <option key={d.key} value={d.text}>
                    {d.text}
                  </option>
                ))}
              </Select>
            </Box>

            <Box>
              <HStack spacing="1" align="center" mb={2}>
                <Text m={0}>Kernel</Text>
                <MaterialSymbol
                  // as={MaterialSymbol}
                  icon="change_history"
                  size={22}
                  // fontSize="1.25em" // scales with text size
                  verticalAlign="text-bottom"
                  fill={0}
                  grade={0}
                  weight={300}
                  aria-hidden // decorative
                />
              </HStack>
              <Select
                value={selectedKernel}
                onChange={handleKernelChange}
                textTransform={"capitalize"}
              >
                {Object.keys(kernels).map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </Select>
            </Box>

            {/* <Box>
              <Text mb={2}></Text>
              <Select value={} onChange={}>
                {Object.keys().map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </Select>
            </Box> */}

            {/* Mean Days Slider */}
            {/* <Box>
              <Text mb={2}>Mean Days</Text>
              <Slider min={1} max={9} value={daysAvgK} onChange={setDaysAvgK}>
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb />
              </Slider>
              <Text mt={2} textAlign="center">
                {daysAvgK}
              </Text>
            </Box> */}
          </VStack>
        </Box>

        {/* Feature Information Section */}
        {/* {featureInfo && (
          <Box mb={8} p={4} boxShadow="md" borderRadius="md">
            <Heading as="h4" size="md" mb={4}>
              Feature Information
            </Heading>
            {featureInfo.featureNameFull && (
              <Text fontWeight="bold" mb={2}>
                {featureInfo.featureNameFull}
              </Text>
            )}
            {featureInfo.featureDetail && (
              <Text>{featureInfo.featureDetail}</Text>
            )}
          </Box>
        )} */}

        {/* Legend for the Scatter Plot */}
        <Box mb={6} p={4}>
          <Heading as="h4" size="md" mb={4}>
            Legend
          </Heading>
          <Box display="flex" flexDirection="column" gap={2}>
            {/* <Box display="flex" alignItems="center">
              <Box
                w="16px"
                h="16px"
                bg="#2b6cb0"
                mr={2}
                borderRadius="50%"
                flexShrink={0}
              ></Box>
              <Text>
                Original data points (blue). Select a day by clicking on its
                data point.
              </Text>
            </Box>
            <Box display="flex" alignItems="center">
              <Box
                w="16px"
                h="16px"
                bg="#E53E3E"
                mr={2}
                borderRadius="50%"
                flexShrink={0}
              ></Box>
              <Text>Mean values (red)</Text>
            </Box> */}
            <Box display="flex" alignItems="center">
              <Box w="16px" h="16px" bg="orange" mr={2} flexShrink={0}></Box>
              <Text>Admission date</Text>
            </Box>
            <Box display="flex" alignItems="center">
              <Box w="16px" h="16px" bg="steelblue" mr={2} flexShrink={0}></Box>
              <Text>Discharge date</Text>
            </Box>
            <Box display="flex" alignItems="center">
              <Box w="20px" h="6px" bg="red" mr={2} flexShrink={0}></Box>
              <Text>Median</Text>
            </Box>
            <Box display="flex" alignItems="center">
              <HStack spacing="1" align="center" mb={2}>
                <MaterialSymbol
                  // as={MaterialSymbol}

                  icon="change_history"
                  size={20}
                  // fontSize="1.25em" // scales with text size
                  verticalAlign="text-bottom"
                  fill={1}
                  grade={0}
                  weight={300}
                  aria-hidden // decorative
                  color="green"
                />
              </HStack>
              <Text ml={2}>Mean</Text>
            </Box>
          </Box>
        </Box>
      </GridItem>

      {/* Scatter Plot */}
      <GridItem order={{ base: 2, md: 1 }}>
        <Box w="100%" p={4}>
          <Heading as="h2" size="lg" mb={8} textAlign={"center"}>
            {/* Total Patients: {totalPatients} */}
          </Heading>
        
          <Grid
            templateColumns="repeat(auto-fit, minmax(440px, max-content))"
            gap={6}
          >
            {sortBy(featureValues, selectedSortBy.key).map((featureDict) => (
              <GridItem
                key={featureDict.key}
                borderWidth="1px"
                borderRadius="md"
                // p={2}
                boxShadow="sm"

                // w="fit-content"

                // alignItems={"center"}
              >
                <Box
                  padding="0.3rem 0rem 0.3rem 1rem"
                  fontWeight={500}
                  fontSize={17}
                >
                  {featureDict.title ?? ""}
                  {featureDict.unit != "" && (
                    <span> | unit: {featureDict.unit}</span>
                  )}
                </Box>
                <Box
                  // bg="white"
                  display="flex"
                  flexDir="row"
                >
                  <KDEPlot
                    kernel={selectedKernel}
                    title={featureDict.key}
                    datasets={featureDict.data}
                    fillCurves={true}
                    fillOpacity={0.15}
                  />

                  <BoxPlot
                    title={featureDict.key}
                    datasets={featureDict.data}
                  />

                  <Box width={180}>
                    p-value: {featureDict.stats?.p_mwu?.toFixed(3)}
                    <Box mt={2}>
                      {featureDict.data?.map((d) => (
                        <GroupSummary data={d} />
                      ))}
                    </Box>
                  </Box>
                </Box>
              </GridItem>
            ))}
          </Grid>
        </Box>
        {/* Pagination Controls: Only show when "All Patients" is selected */}
        {/* {!selectedPatient && (
          <Box mb={10}>
            <HStack justifyContent="space-between">
              <Button
                onClick={prevPage}
                isDisabled={currentPage === 1}
                leftIcon={<ArrowBackIcon />}
              >
                Previous
              </Button>
              <Text>
                Page {currentPage} of{" "}
                {Math.ceil(patientIds.length / patientsPerPage)}
              </Text>
              <Button
                onClick={nextPage}
                isDisabled={
                  currentPage === Math.ceil(patientIds.length / patientsPerPage)
                }
                rightIcon={<ArrowForwardIcon />}
              >
                Next
              </Button>
            </HStack>
          </Box>
        )} */}
      </GridItem>
    </Grid>
  );
};

export default BoxPlots;
